/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// utils
import PublishSubscribeUtils from "utils/publish-subscribe-utils/PublishSubscribeUtils.js";

// constants
const ENVIRONMENTS = {
  development: "DEVELOPMENT",
  production: "PRODUCTION",
  testing: "TESTING",
};
const PREFIX_DEBUG = "[DEBUG]";
const PREFIX_ERROR = "[ERROR]";
const PREFIX_INFO = "[INFO]";
const PREFIX_WARN = "[WARN]";

class LoggerUtils {
  static debugActive = false;
  static environments = { ...ENVIRONMENTS };
  static errorActive = false;
  static infoActive = false;
  static warnActive = false;
  static topicId = PublishSubscribeUtils.createTopic({ name: "LoggerUtils" });

  /**
  * @param {String} environment
  */
  static initialize(environment) {
    switch (String(environment)) {

      // development
      case ENVIRONMENTS.development: {
        LoggerUtils.bypass("[LoggerUtils] - Development");
        this.debugActive = true;
        this.errorActive = true;
        this.infoActive = true;
        this.warnActive = true;
        break;
      }

      // testing
      case ENVIRONMENTS.testing: {
        this.debugActive = false;
        this.errorActive = true;
        this.infoActive = true;
        this.warnActive = false;
        break;
      }

      // production - (!dev && !test)
      default: {
        this.debugActive = false;
        this.errorActive = false;
        this.infoActive = false;
        this.warnActive = false;
      }
    }
  }

  /*====================
  logging
  ====================*/
  static bypass(...args) {
    const logObjects = args instanceof(Array) ? [...args] : [args];
    console.log(...logObjects); // NOSONAR
  }

  static debug(...args) {
    if(this.debugActive) {
      const logObjects = args instanceof(Array) ? [...args] : [args];
      console.debug(PREFIX_DEBUG, ...logObjects); // NOSONAR
      LoggerUtils.publish(LoggerUtils.format(PREFIX_DEBUG, logObjects));
    }
  }

  static error(...args) {
    if(this.errorActive) {
      const logObjects = args instanceof(Array) ? [...args] : [args];
      console.error(PREFIX_ERROR, ...logObjects); // NOSONAR
      LoggerUtils.publish(LoggerUtils.format(PREFIX_ERROR, logObjects));
    }
  }

  static info(...args) {
    if(this.infoActive) {
      const logObjects = args instanceof(Array) ? [...args] : [args];
      console.info(PREFIX_INFO, ...logObjects); // NOSONAR
      LoggerUtils.publish(LoggerUtils.format(PREFIX_INFO, logObjects));
    }
  }

  static warn(...args) {
    if(this.warnActive) {
      const logObjects = args instanceof(Array) ? [...args] : [args];
      console.warn(PREFIX_WARN, ...logObjects); // NOSONAR
      LoggerUtils.publish(LoggerUtils.format(PREFIX_WARN, logObjects));
    }
  }

  /*====================
  formatting
  ====================*/
  static format(prefix = PREFIX_INFO, ...args) {
    const message = [prefix];
    args.forEach((arg) => message.push(JSON.stringify(arg)));
    return message.join(",");
  }

  /*====================
  publish & subscribe
  ====================*/
  /**
  * @param {String} message
  */
   static publish(message) {
    PublishSubscribeUtils.publish(LoggerUtils.topicId, message);
  }

  /**
  * @param {Function} onLog
  * @returns {String}
  */
  static subscribe(onLog) {
    return PublishSubscribeUtils.subscribe(LoggerUtils.topicId, onLog);
  }

  /**
  * @param {String} subscriberId
  */
  static unsubscribe(subscriberId) {
    PublishSubscribeUtils.unsubscribe(LoggerUtils.topicId, subscriberId);
  }

}
export default LoggerUtils;
